import React, { useState } from "react";
import { useLazyQuery } from "@apollo/client";
import { GET_TEST_SITES } from "./graphql";
import Button from "components/base/Button";
import { InlineSpinner } from "components/base/Spinner";
import Errors from "components/base/Errors";
import { Alert } from "components/base/Toast";
import { Switcher } from "components/base/Form";
import { FiCopy, FiCheck } from "react-icons/fi";

// Odoo站点测试面板组件
const OdooSitesPanel = () => {
  // 站点测试状态
  const [testResults, setTestResults] = useState([]);
  const [successResults, setSuccessResults] = useState([]);
  const [copiedIndex, setCopiedIndex] = useState(null);
  const [showOnlySuccess, setShowOnlySuccess] = useState(true);
  const [
    fetchTestSites,
    { loading: loadingTestSites, error: errorTestSites, data: dataTestSites },
  ] = useLazyQuery(GET_TEST_SITES, {
    fetchPolicy: "network-only", // 强制从网络获取，不使用缓存
    onCompleted: (data) => {
      if (data && data.odooTestSites) {
        const allResults = data.odooTestSites;
        setTestResults(allResults);
        // 过滤出成功的URL
        const successfulUrls = allResults.filter((result) => result.success);
        setSuccessResults(successfulUrls);
      }
    },
    onError: (error) => {
      Alert("error", `测试Odoo站点出错: ${error.message}`);
    },
  });

  const handleTestRequest = () => {
    setTestResults([]); // 清除之前的结果
    setSuccessResults([]);
    setCopiedIndex(null);
    // 使用时间戳作为变量，确保每次请求都被视为新请求
    fetchTestSites({
      fetchPolicy: "network-only",
      variables: { timestamp: new Date().getTime() },
    });
  };

  // 复制URL到剪贴板
  const copyToClipboard = (url, index, e) => {
    // 阻止事件冒泡，避免触发父元素的点击事件
    e.stopPropagation();

    navigator.clipboard.writeText(url).then(
      function () {
        setCopiedIndex(index);
        // 3秒后重置图标
        setTimeout(() => {
          setCopiedIndex(null);
        }, 3000);
      },
      function (err) {
        console.error("无法复制URL: ", err);
      },
    );
  };

  // 在新窗口打开URL
  const openUrl = (url) => {
    if (url) {
      window.open(url, "_blank");
    }
  };

  return (
    <div className="card bg-white shadow-md rounded-lg p-6">
      <h2 className="text-xl font-bold mb-4">可用的测试Odoo站点</h2>
      <div className="flex items-center space-x-4 mb-6">
        <Button
          primary
          onClick={handleTestRequest}
          disabled={loadingTestSites}
        >
          {loadingTestSites ? (
            <span className="flex items-center">
              <InlineSpinner size={16} text={null} color="#fff" />
              <span className="ml-2">探测中...</span>
            </span>
          ) : (
            "开始探测"
          )}
        </Button>

        {testResults.length > 0 && (
          <div className="flex items-center ml-4">
            <span className="text-sm text-gray-700 mr-2">
              仅显示可访问的URL
            </span>
            <Switcher
              isOn={showOnlySuccess}
              onChange={() => setShowOnlySuccess(!showOnlySuccess)}
            />
          </div>
        )}
      </div>

      {loadingTestSites && !testResults.length && (
        <div className="flex justify-center my-6">
          <InlineSpinner size={24} />
        </div>
      )}

      {errorTestSites && <Errors error={errorTestSites} />}

      {testResults.length > 0 && (
        <div className="mt-4">
          <p className="text-base font-semibold mb-3 ">
            探测结果 (共探测 {testResults.length} 个URL，{successResults.length}{" "}
            个可用)
          </p>

          <ul className="grid grid-cols-1 gap-2 max-h-[calc(100vh-350px)] overflow-y-auto p-2">
            {(showOnlySuccess ? successResults : testResults).map(
              (result, index) => (
                <li
                  key={index}
                  className={`p-2 rounded border text-sm ${
                    result.success
                      ? "bg-green-50 cursor-pointer hover:bg-green-100"
                      : "bg-red-50"
                  } flex justify-between items-center`}
                  onClick={
                    result.success ? () => openUrl(result.url) : undefined
                  }
                >
                  <div className="flex-1 mr-2">
                    <p>{result.url}</p>
                  </div>
                  {result.success && (
                    <button
                      onClick={(e) => copyToClipboard(result.url, index, e)}
                      className="p-2 bg-gray-100 hover:bg-gray-200 rounded transition-colors flex items-center justify-center"
                      title="复制URL"
                    >
                      {copiedIndex === index ? (
                        <FiCheck className="text-green-600" />
                      ) : (
                        <FiCopy className="text-gray-600" />
                      )}
                    </button>
                  )}
                </li>
              ),
            )}
          </ul>

          {showOnlySuccess && successResults.length === 0 && (
            <p className="text-gray-500 italic mt-2">
              未找到可用的Odoo测试站点
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default OdooSitesPanel;
